/**
 * A new booking link for a booked opening interview — after a no-show, or to let the candidate
 * move it themselves (POST /api/interview-links/bookings/:ref/new-link, see proxy lib/noShow.js).
 *
 * Its own module: interviewLinksApi.ts is at its line budget.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export type NewLinkReason = 'no_show' | 'move';

export interface NewLinkDraft {
  reason: NewLinkReason;
  /** The link the email carries — a fresh booking link, or the candidate's own manage page. */
  url: string;
  opportunityId: string | null;
  candidateName: string;
  language: 'de' | 'en';
  roleTitle: string;
  stageName: string;
  email: Record<'de' | 'en', { subject: string; body: string }>;
  /** False when the Library template is missing or switched off and a plain fallback was used. */
  fromLibrary: boolean;
  /** The Lever note to write once the email has gone. */
  note: string;
}

/**
 * Creates the link (a no-show mints a new one right away) and returns the draft. Nothing is
 * emailed here. A 409 is a refusal with a reason the dialog shows — read, not reported globally.
 */
export async function requestNewBookingLink(
  bookingRef: string,
  reason: NewLinkReason
): Promise<NewLinkDraft> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/interview-links/bookings/${encodeURIComponent(bookingRef)}/new-link`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
      suppressErrorStatuses: [409],
    }
  );
  const data = (await res.json()) as NewLinkDraft & { error?: string };
  if (!res.ok) throw new Error(data?.error || 'Could not create a new link');
  return data;
}
